// src/features/auth/registration/components/AccreditedInvestorDeclarationModal.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stack,
  useTheme,
  useMediaQuery,
  Autocomplete,
  IconButton,
} from '@mui/material';
import { TOUCH, FONT_SIZE, FONT_WEIGHT, ZINDEX } from '@/core/platform/platformStandards';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AgreementPdfViewer, {
  AgreementBusyState,
  openAgreementPdfInNewTab,
} from '@/components/agreements/AgreementPdfViewer';
import {
  agreementModalPaperSx,
  agreementModalTitleSx,
  agreementModalContentSx,
  agreementPdfRegionSx,
  agreementModalFooterStripSx,
  agreementModalActionsSx,
} from '@/components/agreements/agreementModalLayout';
import agreementService from '@/services/agreementService';

const DRAFT_DEBOUNCE_MS = 450;

const AccreditedInvestorDeclarationModal = ({
  open,
  onClose,
  onSign,
  entityName,
  institutionId = null,
  userId = null,
  jobTitles,
  isLoading,
  termsAlreadyAccepted = false,
  preSelectedTitle = null,
  presentation = 'modal',
  qualificationCategories = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isInline = presentation === 'inline';
  const isActive = open || isInline;
  const [attestationEntityName, setAttestationEntityName] = useState('');
  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [declarationPdfUrl, setDeclarationPdfUrl] = useState(null);
  const [declarationContentHash, setDeclarationContentHash] = useState('');
  const [declarationDraftS3Key, setDeclarationDraftS3Key] = useState(null);
  const [declarationVersion, setDeclarationVersion] = useState('1.0');
  const [loadingDeclarationPdf, setLoadingDeclarationPdf] = useState(false);
  const [declarationPdfError, setDeclarationPdfError] = useState(null);
  const [viewerInstanceKey, setViewerInstanceKey] = useState(0);

  const draftRequestRef = useRef(0);
  const draftDebounceRef = useRef(null);
  const pdfViewSessionRef = useRef('');

  const usePersonalizedDraft = Boolean(institutionId || userId);
  const skipTermsGate = termsAlreadyAccepted || isInline;
  const entityReadOnly = skipTermsGate;

  const categories = useMemo(
    () => (Array.isArray(qualificationCategories) ? qualificationCategories : []),
    [qualificationCategories],
  );

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const pdfLoadingMessage = usePersonalizedDraft
    ? 'Personalizing your declaration with your institution name and qualifications…'
    : 'Loading declaration…';

  useEffect(() => {
    if (isActive) {
      if (entityName) {
        setAttestationEntityName(entityName);
      }
      setTermsAccepted(termsAlreadyAccepted || isInline);

      if (preSelectedTitle && jobTitles && jobTitles.length > 0) {
        const matchingTitle = jobTitles.find(
          (t) =>
            t.value === preSelectedTitle ||
            t.display === preSelectedTitle ||
            t.label === preSelectedTitle,
        );
        if (matchingTitle) {
          setTitle(matchingTitle);
        }
      }
    } else {
      setAttestationEntityName('');
      setFullName('');
      setTitle(null);
      setTermsAccepted(false);
      setError('');
      setIsSubmitting(false);
      setDeclarationPdfUrl(null);
      setDeclarationContentHash('');
      setDeclarationDraftS3Key(null);
      setDeclarationPdfError(null);
      setLoadingDeclarationPdf(false);
      pdfViewSessionRef.current = '';
    }
  }, [isActive, entityName, termsAlreadyAccepted, isInline, preSelectedTitle, jobTitles]);

  const refreshPersonalizedDraft = useCallback(
    async (entity, categoryIds) => {
      if (!usePersonalizedDraft || !categoryIds.length || !entity.trim()) {
        setDeclarationPdfUrl(null);
        setDeclarationContentHash('');
        setDeclarationDraftS3Key(null);
        setDeclarationPdfError(null);
        setLoadingDeclarationPdf(false);
        return;
      }

      const requestId = draftRequestRef.current + 1;
      draftRequestRef.current = requestId;
      setLoadingDeclarationPdf(true);
      setDeclarationPdfError(null);

      try {
        const res = await agreementService.loadAccreditedPersonalizedDraft({
          attestationEntityName: entity.trim(),
          qualificationCategories: categoryIds,
        });
        if (draftRequestRef.current !== requestId) {
          return;
        }
        if (res?.url) {
          setDeclarationPdfUrl(res.url);
          setDeclarationContentHash(res.content_hash || '');
          setDeclarationDraftS3Key(res.draft_s3_key || null);
          setDeclarationVersion(res.agreement_version || '1.0');
        } else {
          setDeclarationPdfError('Personalized declaration PDF was not returned. Please try again.');
        }
      } catch (err) {
        if (draftRequestRef.current !== requestId) {
          return;
        }
        console.error('Failed to load personalized accredited declaration PDF:', err);
        setDeclarationPdfError(
          err.message || 'Failed to personalize your declaration. Please try again.',
        );
        setDeclarationPdfUrl(null);
        setDeclarationContentHash('');
        setDeclarationDraftS3Key(null);
      } finally {
        if (draftRequestRef.current === requestId) {
          setLoadingDeclarationPdf(false);
        }
      }
    },
    [usePersonalizedDraft],
  );

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }

    if (usePersonalizedDraft) {
      const canPersonalize = categories.length > 0 && attestationEntityName.trim();
      if (canPersonalize) {
        setLoadingDeclarationPdf(true);
        setDeclarationPdfError(null);
      }
      if (draftDebounceRef.current) {
        clearTimeout(draftDebounceRef.current);
      }
      draftDebounceRef.current = setTimeout(() => {
        void refreshPersonalizedDraft(attestationEntityName, categories);
      }, DRAFT_DEBOUNCE_MS);
      return () => {
        if (draftDebounceRef.current) {
          clearTimeout(draftDebounceRef.current);
        }
      };
    }

    if (declarationPdfUrl || loadingDeclarationPdf) {
      return undefined;
    }

    setLoadingDeclarationPdf(true);
    setDeclarationPdfError(null);
    agreementService
      .getPublicAgreementPdfUrl('accredited')
      .then((res) => {
        if (res?.url) {
          setDeclarationPdfUrl(res.url);
          setDeclarationContentHash(res.content_hash || '');
          setDeclarationVersion(res.agreement_version || '1.0');
        } else {
          setDeclarationPdfError('Agreement PDF is not available at this time.');
        }
      })
      .catch((err) => {
        console.error('Failed to load accredited investor declaration PDF:', err);
        setDeclarationPdfError('Failed to load the declaration. Please close this window and try again.');
      })
      .finally(() => setLoadingDeclarationPdf(false));

    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- public PDF loads once; personalized path debounces entity/categories
  }, [isActive, usePersonalizedDraft, attestationEntityName, categories, refreshPersonalizedDraft]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (!declarationPdfUrl || loadingDeclarationPdf) {
      return;
    }
    if (pdfViewSessionRef.current === declarationPdfUrl) {
      return;
    }
    pdfViewSessionRef.current = declarationPdfUrl;
    setViewerInstanceKey((key) => key + 1);
  }, [isActive, declarationPdfUrl, loadingDeclarationPdf]);

  const validateInputs = () => {
    if (!skipTermsGate && !termsAccepted) {
      return 'You must accept the Terms and Conditions';
    }
    if (!attestationEntityName.trim()) {
      return 'Entity name is required';
    }
    if (!fullName.trim()) {
      return 'Please enter your full name';
    }
    const nameParts = fullName.trim().split(/\s+/);
    if (nameParts.length < 2) {
      return 'Please enter your full name (first and last name)';
    }
    const namePattern = /^[a-zA-Z\s'-]+$/;
    if (!namePattern.test(fullName)) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    if (!title) {
      return 'Please select your title';
    }
    if (usePersonalizedDraft && categories.length === 0) {
      return 'Qualification categories are missing. Complete your institution profile first.';
    }
    if (usePersonalizedDraft && (!declarationDraftS3Key || !declarationContentHash)) {
      return 'Your personalized declaration is still loading. Please wait a moment.';
    }
    return '';
  };

  const handleSign = async () => {
    const validationError = validateInputs();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      let ipAddress = 'unknown';
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch (ipError) {
        console.warn('Could not fetch IP address:', ipError);
      }

      const signatureData = {
        attestationEntityName: attestationEntityName.trim(),
        fullName: fullName.trim(),
        title: title.value,
        titleDisplay: title.display || title.label,
        timestamp: new Date().toISOString(),
        ipAddress,
        userAgent: navigator.userAgent,
        agreementType: 'accredited_investor_declaration',
        agreementVersion: declarationVersion,
        agreementContentHash: declarationContentHash,
        draftS3Key: declarationDraftS3Key,
        qualificationCategories: categories,
        termsAccepted,
        termsAlreadyAccepted,
      };

      await onSign(signatureData);
    } catch (err) {
      setError('Failed to submit declaration. Please try again.');
      console.error('Error submitting declaration:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSubmitting) return;
    onClose();
  };

  const viewerError =
    declarationPdfError ||
    (usePersonalizedDraft && categories.length === 0
      ? 'Qualification categories from your institution profile are required to preview your personalized declaration.'
      : '');

  const awaitingPersonalizedPdf =
    usePersonalizedDraft &&
    categories.length > 0 &&
    attestationEntityName.trim() &&
    !declarationPdfUrl &&
    !declarationPdfError;

  const pdfViewerLoading = loadingDeclarationPdf || awaitingPersonalizedPdf;

  const signDisabled =
    isSubmitting ||
    isLoading ||
    pdfViewerLoading ||
    !!viewerError ||
    !declarationPdfUrl ||
    (!skipTermsGate && !termsAccepted) ||
    !attestationEntityName.trim() ||
    !fullName.trim() ||
    !title ||
    (usePersonalizedDraft &&
      (categories.length === 0 || !declarationDraftS3Key || !declarationContentHash));

  const signatureStrip = (
    <Box sx={agreementModalFooterStripSx}>
      {!skipTermsGate && !termsAccepted && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontSize: FONT_SIZE.sm }}>
            Please accept the Terms and Conditions above to enable the signature fields.
          </Typography>
        </Alert>
      )}

      {!entityReadOnly && (
        <TextField
          fullWidth
          size="small"
          label="Entity name"
          value={attestationEntityName}
          onChange={(e) => setAttestationEntityName(e.target.value)}
          disabled={isSubmitting || (!skipTermsGate && !termsAccepted)}
          sx={{ mb: 1 }}
        />
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1, fontWeight: FONT_WEIGHT.medium }}
      >
        Electronic signature *
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr auto',
            md: '1.2fr 1fr auto',
          },
          gap: 1,
          alignItems: 'start',
        }}
      >
        <TextField
          size="small"
          label="Full legal name"
          placeholder="First and last name"
          value={fullName}
          onChange={(e) => {
            setFullName(e.target.value);
            if (error) setError('');
          }}
          error={!!error && error.includes('name')}
          disabled={isSubmitting || (!skipTermsGate && !termsAccepted)}
          required
          sx={{
            '& .MuiInputBase-input': {
              fontFamily: 'cursive',
              fontSize: FONT_SIZE.md,
              color: 'primary.main',
            },
          }}
        />
        <Autocomplete
          size="small"
          value={title}
          onChange={(_, newValue) => {
            setTitle(newValue);
            if (error) setError('');
          }}
          options={jobTitles || []}
          getOptionLabel={(option) => option.display || option.label || ''}
          isOptionEqualToValue={(a, b) => a?.value === b?.value}
          disabled={isSubmitting || (!skipTermsGate && !termsAccepted)}
          disablePortal={!isInline}
          slotProps={{
            popper: {
              sx: { zIndex: ZINDEX.modal + 150 },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Title"
              required
              error={!!error && error.includes('title')}
              placeholder="Select title"
            />
          )}
        />
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            minHeight: 40,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            minWidth: { sm: 96 },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Date
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: FONT_WEIGHT.semibold, fontSize: FONT_SIZE.sm }}>
            {currentDate}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.75 }}>
          {error}
        </Typography>
      )}
    </Box>
  );

  const documentBody = isLoading ? (
    <AgreementBusyState message="Loading…" minHeight="100%" />
  ) : isSubmitting ? (
    <AgreementBusyState message="Recording your signature and saving your declaration…" minHeight="100%" size={32} />
  ) : (
    <>
      <Box sx={agreementPdfRegionSx}>
        <AgreementPdfViewer
          pdfUrl={declarationPdfUrl}
          isLoading={pdfViewerLoading}
          loadingMessage={pdfLoadingMessage}
          error={pdfViewerLoading ? '' : viewerError}
          title="Accredited Investor Declaration"
          minHeight="100%"
          viewerInstanceKey={viewerInstanceKey}
          active={isActive && !isSubmitting && !isLoading}
        />
      </Box>
      {signatureStrip}
    </>
  );

  const actionButtons = (
    <>
      {!isInline && (
        <Button
          onClick={handleCancel}
          disabled={isSubmitting}
          variant="outlined"
          sx={{ minWidth: 100, minHeight: TOUCH.minSize }}
        >
          Cancel
        </Button>
      )}
      <Button
        onClick={handleSign}
        disabled={signDisabled}
        variant="contained"
        startIcon={isSubmitting ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        sx={{ minWidth: 180, minHeight: TOUCH.minSize }}
      >
        {isSubmitting ? 'Submitting...' : 'Attest & Sign'}
      </Button>
    </>
  );

  if (isInline) {
    if (!isActive) {
      return null;
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'min(78vh, 820px)', md: '72vh' },
          minHeight: 520,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        {attestationEntityName && (
          <Box sx={{ px: { xs: 1.5, sm: 2 }, pt: 1, pb: 0.5, flexShrink: 0 }}>
            <Typography variant="caption" color="text.secondary">
              Institution
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: FONT_WEIGHT.medium }} noWrap>
              {attestationEntityName}
            </Typography>
          </Box>
        )}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {documentBody}
        </Box>
        <Box
          sx={{
            ...agreementModalActionsSx,
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          {actionButtons}
        </Box>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleCancel}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      sx={{ zIndex: ZINDEX.modal }}
      PaperProps={{
        sx: agreementModalPaperSx(isMobile, theme),
      }}
    >
      <DialogTitle sx={agreementModalTitleSx}>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
            <GavelIcon sx={{ mr: 1, color: 'primary.main', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: FONT_WEIGHT.semibold, lineHeight: 1.3 }}>
                Accredited Investor Declaration
              </Typography>
              {attestationEntityName && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {attestationEntityName}
                </Typography>
              )}
            </Box>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {declarationPdfUrl && !isSubmitting && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                onClick={() => openAgreementPdfInNewTab(declarationPdfUrl)}
                disabled={loadingDeclarationPdf}
                sx={{
                  minHeight: TOUCH.minSize,
                  display: { xs: 'none', sm: 'inline-flex' },
                }}
              >
                Open in new tab
              </Button>
            )}
            <IconButton
              onClick={handleCancel}
              disabled={isSubmitting}
              aria-label="Close"
              sx={{ minWidth: TOUCH.minSize, minHeight: TOUCH.minSize }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={agreementModalContentSx}>
        {documentBody}
      </DialogContent>

      <DialogActions sx={agreementModalActionsSx}>{actionButtons}</DialogActions>
    </Dialog>
  );
};

AccreditedInvestorDeclarationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSign: PropTypes.func.isRequired,
  entityName: PropTypes.string,
  institutionId: PropTypes.string,
  userId: PropTypes.string,
  jobTitles: PropTypes.array,
  isLoading: PropTypes.bool,
  termsAlreadyAccepted: PropTypes.bool,
  preSelectedTitle: PropTypes.string,
  presentation: PropTypes.oneOf(['modal', 'inline']),
  qualificationCategories: PropTypes.arrayOf(PropTypes.string),
};

AccreditedInvestorDeclarationModal.defaultProps = {
  entityName: '',
  institutionId: null,
  userId: null,
  jobTitles: [],
  isLoading: false,
  termsAlreadyAccepted: false,
  preSelectedTitle: null,
  presentation: 'modal',
  qualificationCategories: [],
};

export default AccreditedInvestorDeclarationModal;
